// =============================================================================
// src/chrome/copilot-sidebar/insight-panel-controller.ts
// Controla o card "Contexto atual": troca de insight por secao com animacao swap.
//
// Proveniencia: porte FIEL do IIFE inline de unified.html linhas 2948-3106
//   (splitDetail, updateInsight, is-swapping setTimeout 180ms, bootstrap).
//   INSIGHTS agora vem de src/data (SECTION_INSIGHTS). O painel preenchido por
//   esta classe substitui o conteudo estatico do markup.
//
// SRP: so o painel de contexto. Collapse/reopen e scroll-spy vivem em arquivos
//   irmaos; a acao primaria/Ctrl+J sao ligadas por eles consumindo este controller.
// =============================================================================

import type { SectionId, SectionInsight, SectionInsightsMap } from '../../app/types';
import type { CopilotElementRefs } from './copilot-element-refs';

/** Duracao do swap antes de reescrever o conteudo (unified.html 3030: 180ms). */
const SWAP_DELAY_MS = 180;

/** Limiar de confianca baixa que dispara o estado .is-low-confidence (3017). */
const LOW_CONFIDENCE_THRESHOLD = 75;

/** Resultado de splitDetail: dado em foco + leitura completa (com <strong> intacto). */
interface DetailFocus {
  readonly dado: string;
  readonly rest: string;
}

/**
 * Painel "Contexto atual" do Copiloto. Troca o insight quando a secao muda,
 *   com a animacao de swap (is-swapping) salvo se prefers-reduced-motion.
 */
export class CopilotInsightPanelController {
  private currentSection: SectionId = 'mapa-eleitoral';

  /**
   * @param refs Referencias de DOM coletadas da sidebar.
   * @param insights Mapa de insights por secao (SECTION_INSIGHTS).
   * @param reduceMotion Se true, troca sem animacao (matchMedia reduced-motion).
   */
  constructor(
    private readonly refs: CopilotElementRefs,
    private readonly insights: SectionInsightsMap,
    private readonly reduceMotion: boolean,
  ) {}

  /** Secao atualmente exibida no painel (espelha `currentSection` do IIFE). */
  get activeSection(): SectionId {
    return this.currentSection;
  }

  /** Preenche o painel com a secao inicial (porte de bootstrap(), 3089-3106). */
  bootstrap(): void {
    const insight = this.insights[this.currentSection];
    this.writeInsight(this.currentSection, insight);
  }

  /**
   * Troca o insight exibido. No-op se ja for a secao atual (3.996). Anima via
   *   is-swapping (180ms) exceto sob reduced-motion (porte de updateInsight).
   */
  updateInsight(sectionId: SectionId): void {
    if (sectionId === this.currentSection) return;
    const insight = this.insights[sectionId];
    if (!insight) return;
    this.currentSection = sectionId;
    this.markNavItemsActive(sectionId);
    this.swapWithAnimation(sectionId, insight);
  }

  /**
   * Marca o nav-item ativo. DESVIO PRESERVADO: navItems e sempre vazio (o seletor
   *   `.copilot-nav-item` nao existe no markup), entao este loop e no-op — igual
   *   ao original (unified.html 3001-3006). Mantido para fidelidade total.
   */
  private markNavItemsActive(sectionId: SectionId): void {
    this.refs.navItems.forEach((navItem) => {
      const active = navItem.dataset.section === sectionId;
      navItem.classList.toggle('active', active);
      if (active) navItem.setAttribute('aria-current', 'true');
      else navItem.removeAttribute('aria-current');
    });
  }

  /** Aplica a animacao de swap antes de reescrever (updateInsight, 3025-3030). */
  private swapWithAnimation(sectionId: SectionId, insight: SectionInsight): void {
    if (this.reduceMotion) {
      this.writeInsight(sectionId, insight);
      return;
    }
    this.refs.context.classList.add('is-swapping');
    setTimeout(() => {
      this.writeInsight(sectionId, insight);
      requestAnimationFrame(() =>
        this.refs.context.classList.remove('is-swapping'),
      );
    }, SWAP_DELAY_MS);
  }

  /** Reescreve os campos do painel (corpo comum de swap() e bootstrap()). */
  private writeInsight(sectionId: SectionId, insight: SectionInsight): void {
    const { dado, rest } = splitDetail(insight.detail || '');
    this.writeContextLabel(sectionId, insight);
    this.writeBody(dado, rest, insight);
    this.writeConfidence(insight);
    this.writeAction(insight);
  }

  /** Escreve o rotulo da secao. Prefixo NN· vem de sectionOrder — sempre vazio. */
  private writeContextLabel(sectionId: SectionId, insight: SectionInsight): void {
    if (!this.refs.sectionLabel) return;
    // DESVIO PRESERVADO: sectionOrder deriva de navItems (vazio), logo prefix=''
    //   sempre — sem "01 · " no rotulo (unified.html 3010-3011 + 2948-2949).
    const prefix = sectionOrderPrefix(sectionId);
    /* v8 ignore next 2 -- sectionOrderPrefix sempre '' (DESVIO PRESERVADO); ramo prefix truthy inalcançável */
    this.refs.sectionLabel.textContent =
      (prefix ? prefix + ' · ' : '') + insight.label;
  }

  /** Escreve dado em foco, headline e leitura (innerHTML preserva <strong>). */
  private writeBody(dado: string, rest: string, insight: SectionInsight): void {
    if (this.refs.dado) this.refs.dado.textContent = dado;
    if (this.refs.headline) this.refs.headline.textContent = insight.headline;
    if (this.refs.detail) this.refs.detail.innerHTML = rest || '';
  }

  /** Atualiza barra/rotulo de confianca e o estado is-low-confidence + chip. */
  private writeConfidence(insight: SectionInsight): void {
    if (this.refs.confBar) this.refs.confBar.style.width = insight.confidence + '%';
    if (this.refs.confLabel)
      this.refs.confLabel.textContent = 'confiança ' + insight.confidence + '%';
    const lowConfidence = insight.confidence < LOW_CONFIDENCE_THRESHOLD;
    this.refs.copilot.classList.toggle('is-low-confidence', lowConfidence);
    if (this.refs.attentionChip)
      this.refs.attentionChip.textContent = lowConfidence ? 'atenção' : 'confiança alta';
  }

  /** Atualiza texto e data-prompt da acao primaria (3020-3022). */
  private writeAction(insight: SectionInsight): void {
    if (this.refs.actionText)
      this.refs.actionText.textContent = (insight.primary || '').replace(/\s*→\s*$/, '');
    if (this.refs.actionPrimary)
      this.refs.actionPrimary.dataset.prompt = insight.primaryPrompt;
    if (this.refs.why) this.refs.why.textContent = insight.why || '';
  }
}

/**
 * Expoe o primeiro <strong>…</strong> como "Dado em foco"; devolve o detail
 *   inteiro (com o strong intacto) como leitura (porte de splitDetail, 2989-2993).
 */
function splitDetail(detailHtml: string): DetailFocus {
  const strongMatch = detailHtml.match(/<strong>([\s\S]*?)<\/strong>/i);
  const dado = strongMatch
    /* v8 ignore next -- grupo 1 do regex sempre definido quando há match; guarda noUncheckedIndexedAccess CLAUDE.md rule 11 */
    ? (strongMatch[1] ?? '').replace(/<[^>]+>/g, '').trim()
    : '—';
  return { dado, rest: detailHtml };
}

/**
 * Prefixo "NN" da secao no rotulo. DESVIO PRESERVADO: derivava de sectionOrder,
 *   populado a partir de `.copilot-nav-item` (inexistente). Logo o objeto era {}
 *   e sectionOrder[sectionId] era undefined → prefixo vazio. Mantido como '' fixo.
 */
function sectionOrderPrefix(_sectionId: SectionId): string {
  return '';
}
